const WATER_NETWORK_WIDGET_TYPES = new Set(['water-tank', 'pipe', 'elbow', 'water-pump']);

export const WATER_NETWORK_SNAP_TOLERANCE = 18;
export const WATER_NETWORK_CONNECTION_TOLERANCE = 20;
export const WATER_NETWORK_FREEFORM_SEARCH_STEP = 10;

const MIN_CONNECTION_SPAN = 22;
const MIN_CONNECTION_OVERLAP_RATIO = 0.18;
const CONNECTION_EDGE_BLEED = 14;

const WATER_NETWORK_MIN_DIMENSIONS = {
	pipe: { width: 132, height: 78 },
	elbow: { width: 132, height: 96 },
	'water-pump': { width: 172, height: 126 },
	'water-tank': { width: 164, height: 178 },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getWidgetType = (widgetOrType) => (
	typeof widgetOrType === 'string'
		? widgetOrType
		: widgetOrType?.type
);

export const isWaterNetworkWidgetType = (widgetOrType) =>
	WATER_NETWORK_WIDGET_TYPES.has(getWidgetType(widgetOrType));

export const isWidgetFreeMovementEnabled = (widgetOrProps) => {
	if (!widgetOrProps) {
		return false;
	}

	const explicitProp = widgetOrProps?.freeMovement;
	const extraProp = widgetOrProps?.extra_props?.freeMovement;

	if (typeof explicitProp === 'boolean') return explicitProp;
	if (typeof extraProp === 'boolean') return extraProp;

	return isWaterNetworkWidgetType(widgetOrProps);
};

export const canWaterWidgetsShareSpace = (subjectWidgetOrType, otherWidgetOrType) =>
	isWaterNetworkWidgetType(subjectWidgetOrType)
	&& isWaterNetworkWidgetType(otherWidgetOrType)
	&& isWidgetFreeMovementEnabled(subjectWidgetOrType)
	&& isWidgetFreeMovementEnabled(otherWidgetOrType);

export const isWaterNetworkLinkEnabled = (widgetOrProps) => {
	if (!widgetOrProps || typeof widgetOrProps !== 'object') {
		return true;
	}

	const explicitProp = widgetOrProps?.linkToNearby;
	const extraProp = widgetOrProps?.extra_props?.linkToNearby;

	if (typeof explicitProp === 'boolean') return explicitProp;
	if (typeof extraProp === 'boolean') return extraProp;
	return true;
};

export const getWaterNetworkMinDimensions = (widgetOrType) => {
	const widgetType = getWidgetType(widgetOrType);
	return WATER_NETWORK_MIN_DIMENSIONS[widgetType] || null;
};

const toFiniteNumber = (value, fallback = 0) => {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : fallback;
};

const getOverlapLength = (firstStart, firstEnd, secondStart, secondEnd) =>
	Math.max(0, Math.min(firstEnd, secondEnd) - Math.max(firstStart, secondStart));

const getMinimumAlignmentOverlap = (firstSpan, secondSpan) =>
	Math.max(
		MIN_CONNECTION_SPAN,
		Math.min(firstSpan, secondSpan) * MIN_CONNECTION_OVERLAP_RATIO,
	);

const getWidgetWaterColor = (widget) =>
	String(
		widget?.extra_props?.color
		|| widget?.color
		|| 'rgba(0, 173, 239, 0.52)',
	).trim();

const getWidgetShellColor = (widget) =>
	String(
		widget?.extra_props?.pipeColor
		|| widget?.extra_props?.frameBorderColor
		|| widget?.extra_props?.cardBackgroundColor
		|| widget?.pipeColor
		|| 'rgba(104, 145, 172, 0.72)',
	).trim();

const getWaterWidgetPipeThickness = (widget) => {
	const widgetType = getWidgetType(widget);
	const configuredPipeHeight = toFiniteNumber(
		widget?.extra_props?.pipeHeight ?? widget?.pipeHeight,
		NaN,
	);

	if (widgetType === 'pipe') {
		const pipeHeight = Number.isFinite(configuredPipeHeight) ? configuredPipeHeight : 70;
		return clamp(pipeHeight * 0.66, 20, 62);
	}

	if (widgetType === 'elbow') {
		const pipeHeight = Number.isFinite(configuredPipeHeight) ? configuredPipeHeight : 70;
		return clamp(pipeHeight * 0.66, 20, 62);
	}

	if (widgetType === 'water-pump') {
		const pipeHeight = Number.isFinite(configuredPipeHeight) ? configuredPipeHeight : 70;
		return clamp(pipeHeight * 0.6222857143, 20, 54);
	}

	return null;
};

const getWaterConnectionThickness = (widgetA, widgetB, overlapSpan) => {
	const fallbackThickness = clamp(Math.min(overlapSpan * 0.46, 48), 20, 48);
	const desiredThicknesses = [
		getWaterWidgetPipeThickness(widgetA),
		getWaterWidgetPipeThickness(widgetB),
	].filter((value) => Number.isFinite(value));

	if (desiredThicknesses.length === 0) {
		return fallbackThickness;
	}

	const averageThickness = desiredThicknesses.reduce((sum, value) => sum + value, 0) / desiredThicknesses.length;
	const maxThickness = clamp(Math.min(overlapSpan * 0.62, 56), 20, 56);
	return clamp(averageThickness, 20, maxThickness);
};

export const getWaterWidgetFlowStrength = (widget) => {
	const widgetType = getWidgetType(widget);
	const rawValue = widget?.extra_props?.value ?? widget?.value;
	const numericValue = Number(rawValue);

	if (!Number.isFinite(numericValue)) {
		return rawValue ? 1 : 0;
	}

	if (widgetType === 'water-tank') {
		const minValue = toFiniteNumber(widget?.extra_props?.min, 0);
		const maxValue = toFiniteNumber(widget?.extra_props?.max, Math.max(minValue + 100, 100));
		const range = maxValue - minValue;
		if (!(range > 0)) {
			return numericValue > minValue ? 1 : 0;
		}
		return clamp((numericValue - minValue) / range, 0, 1);
	}

	const threshold = Math.abs(toFiniteNumber(widget?.extra_props?.flowThreshold, 0));
	return Math.abs(numericValue) > threshold ? 1 : 0;
};

const getWaterConnectionFlowStrength = (widgetA, widgetB) => {
	const widgets = [widgetA, widgetB].filter(Boolean);
	const activeFlowWidgets = widgets.filter((widget) => getWidgetType(widget) !== 'water-tank');

	if (activeFlowWidgets.length > 0) {
		return Math.max(...activeFlowWidgets.map(getWaterWidgetFlowStrength), 0);
	}

	return Math.max(...widgets.map(getWaterWidgetFlowStrength), 0);
};

const buildConnectionRecord = (widgetA, widgetB, orientation, gap, overlapSpan, anchorStart) => {
	const overlapCenter = anchorStart + (overlapSpan / 2);
	const thickness = getWaterConnectionThickness(widgetA, widgetB, overlapSpan);
	const connectorDepth = Math.max(18, Math.abs(gap) + (CONNECTION_EDGE_BLEED * 2));

	const isFirstBeforeSecond = orientation === 'horizontal'
		? widgetA.__rect.right <= widgetB.__rect.left
		: widgetA.__rect.bottom <= widgetB.__rect.top;

	const flowStrength = getWaterConnectionFlowStrength(widgetA, widgetB);

	const waterColor = getWaterWidgetWaterColorForPair(widgetA, widgetB);
	const shellColor = getWaterWidgetShellColorForPair(widgetA, widgetB);

	const seamStart = orientation === 'horizontal'
		? Math.min(widgetA.__rect.right, widgetB.__rect.left)
		: Math.min(widgetA.__rect.bottom, widgetB.__rect.top);
	const seamEnd = orientation === 'horizontal'
		? Math.max(widgetA.__rect.right, widgetB.__rect.left)
		: Math.max(widgetA.__rect.bottom, widgetB.__rect.top);

	if (orientation === 'horizontal') {
		return {
			id: `${widgetA.id}-${widgetB.id}-horizontal`,
			orientation,
			x: seamStart - CONNECTION_EDGE_BLEED,
			y: overlapCenter - (thickness / 2),
			width: Math.max(connectorDepth, (seamEnd - seamStart) + (CONNECTION_EDGE_BLEED * 2)),
			height: thickness,
			flowDirection: isFirstBeforeSecond ? 'left-to-right' : 'right-to-left',
			active: flowStrength > 0.05,
			flowStrength,
			waterColor,
			shellColor,
			distance: Math.abs(gap),
			bleed: CONNECTION_EDGE_BLEED,
		};
	}

	return {
		id: `${widgetA.id}-${widgetB.id}-vertical`,
		orientation,
		x: overlapCenter - (thickness / 2),
		y: seamStart - CONNECTION_EDGE_BLEED,
		width: thickness,
		height: Math.max(connectorDepth, (seamEnd - seamStart) + (CONNECTION_EDGE_BLEED * 2)),
		flowDirection: isFirstBeforeSecond ? 'top-to-bottom' : 'bottom-to-top',
		active: flowStrength > 0.05,
		flowStrength,
		waterColor,
		shellColor,
		distance: Math.abs(gap),
		bleed: CONNECTION_EDGE_BLEED,
	};
};

const getWaterWidgetWaterColorForPair = (widgetA, widgetB) => (
	getWaterWidgetFlowStrength(widgetA) >= getWaterWidgetFlowStrength(widgetB)
		? getWidgetWaterColor(widgetA)
		: getWidgetWaterColor(widgetB)
);

const getWaterWidgetShellColorForPair = (widgetA, widgetB) => (
	getWaterWidgetFlowStrength(widgetA) >= getWaterWidgetFlowStrength(widgetB)
		? getWidgetShellColor(widgetA)
		: getWidgetShellColor(widgetB)
);

export const snapWaterNetworkMoveRect = (
	rect,
	subjectWidgetOrType,
	widgets,
	getWidgetRectPx,
	metrics,
	currentWidgetId = null,
	tolerance = WATER_NETWORK_SNAP_TOLERANCE,
) => {
	if (
		!isWaterNetworkWidgetType(subjectWidgetOrType)
		|| !isWidgetFreeMovementEnabled(subjectWidgetOrType)
		|| !isWaterNetworkLinkEnabled(subjectWidgetOrType)
		|| !Array.isArray(widgets)
		|| typeof getWidgetRectPx !== 'function'
	) {
		return rect;
	}

	const candidateRect = {
		left: toFiniteNumber(rect?.left, 0),
		top: toFiniteNumber(rect?.top, 0),
		width: Math.max(1, toFiniteNumber(rect?.width, 0)),
		height: Math.max(1, toFiniteNumber(rect?.height, 0)),
	};
	const candidateRight = candidateRect.left + candidateRect.width;
	const candidateBottom = candidateRect.top + candidateRect.height;

	let bestShiftX = null;
	let bestShiftY = null;

	widgets.forEach((widget) => {
		if (
			!widget
			|| widget.id === currentWidgetId
			|| !isWaterNetworkWidgetType(widget)
			|| !isWidgetFreeMovementEnabled(widget)
			|| !isWaterNetworkLinkEnabled(widget)
		) {
			return;
		}

		const otherRect = getWidgetRectPx(widget, metrics);
		const otherRight = otherRect.left + otherRect.width;
		const otherBottom = otherRect.top + otherRect.height;

		const overlapY = getOverlapLength(candidateRect.top, candidateBottom, otherRect.top, otherBottom);
		const minOverlapY = getMinimumAlignmentOverlap(candidateRect.height, otherRect.height);
		if (overlapY >= minOverlapY) {
			const leftDelta = otherRight - candidateRect.left;
			const rightDelta = otherRect.left - candidateRight;

			if (Math.abs(leftDelta) <= tolerance && (bestShiftX === null || Math.abs(leftDelta) < Math.abs(bestShiftX))) {
				bestShiftX = leftDelta;
			}

			if (Math.abs(rightDelta) <= tolerance && (bestShiftX === null || Math.abs(rightDelta) < Math.abs(bestShiftX))) {
				bestShiftX = rightDelta;
			}
		}

		const overlapX = getOverlapLength(candidateRect.left, candidateRight, otherRect.left, otherRight);
		const minOverlapX = getMinimumAlignmentOverlap(candidateRect.width, otherRect.width);
		if (overlapX >= minOverlapX) {
			const topDelta = otherBottom - candidateRect.top;
			const bottomDelta = otherRect.top - candidateBottom;

			if (Math.abs(topDelta) <= tolerance && (bestShiftY === null || Math.abs(topDelta) < Math.abs(bestShiftY))) {
				bestShiftY = topDelta;
			}

			if (Math.abs(bottomDelta) <= tolerance && (bestShiftY === null || Math.abs(bottomDelta) < Math.abs(bestShiftY))) {
				bestShiftY = bottomDelta;
			}
		}
	});

	return {
		...candidateRect,
		left: candidateRect.left + (bestShiftX || 0),
		top: candidateRect.top + (bestShiftY || 0),
	};
};

export const snapWaterNetworkResizeRect = (
	rect,
	subjectWidgetOrType,
	widgets,
	getWidgetRectPx,
	metrics,
	currentWidgetId = null,
	tolerance = WATER_NETWORK_SNAP_TOLERANCE,
) => {
	if (
		!isWaterNetworkWidgetType(subjectWidgetOrType)
		|| !isWidgetFreeMovementEnabled(subjectWidgetOrType)
		|| !isWaterNetworkLinkEnabled(subjectWidgetOrType)
		|| !Array.isArray(widgets)
		|| typeof getWidgetRectPx !== 'function'
	) {
		return rect;
	}

	const candidateRect = {
		left: toFiniteNumber(rect?.left, 0),
		top: toFiniteNumber(rect?.top, 0),
		width: Math.max(1, toFiniteNumber(rect?.width, 0)),
		height: Math.max(1, toFiniteNumber(rect?.height, 0)),
	};
	const candidateRight = candidateRect.left + candidateRect.width;
	const candidateBottom = candidateRect.top + candidateRect.height;

	let bestWidthDelta = 0;
	let bestHeightDelta = 0;

	widgets.forEach((widget) => {
		if (
			!widget
			|| widget.id === currentWidgetId
			|| !isWaterNetworkWidgetType(widget)
			|| !isWidgetFreeMovementEnabled(widget)
			|| !isWaterNetworkLinkEnabled(widget)
		) {
			return;
		}

		const otherRect = getWidgetRectPx(widget, metrics);
		const overlapY = getOverlapLength(
			candidateRect.top,
			candidateBottom,
			otherRect.top,
			otherRect.top + otherRect.height,
		);
		const minOverlapY = getMinimumAlignmentOverlap(candidateRect.height, otherRect.height);
		if (overlapY >= minOverlapY) {
			const widthDelta = otherRect.left - candidateRight;
			if (Math.abs(widthDelta) <= tolerance && Math.abs(widthDelta) < Math.abs(bestWidthDelta || Infinity)) {
				bestWidthDelta = widthDelta;
			}
		}

		const overlapX = getOverlapLength(
			candidateRect.left,
			candidateRight,
			otherRect.left,
			otherRect.left + otherRect.width,
		);
		const minOverlapX = getMinimumAlignmentOverlap(candidateRect.width, otherRect.width);
		if (overlapX >= minOverlapX) {
			const heightDelta = otherRect.top - candidateBottom;
			if (Math.abs(heightDelta) <= tolerance && Math.abs(heightDelta) < Math.abs(bestHeightDelta || Infinity)) {
				bestHeightDelta = heightDelta;
			}
		}
	});

	return {
		...candidateRect,
		width: Math.max(1, candidateRect.width + bestWidthDelta),
		height: Math.max(1, candidateRect.height + bestHeightDelta),
	};
};

export const buildWaterNetworkConnections = (
	widgets,
	getWidgetRectPx,
	metrics,
	tolerance = WATER_NETWORK_CONNECTION_TOLERANCE,
) => {
	if (!Array.isArray(widgets) || typeof getWidgetRectPx !== 'function') {
		return [];
	}

	const waterWidgets = widgets
		.filter((widget) => isWaterNetworkWidgetType(widget) && isWaterNetworkLinkEnabled(widget))
		.map((widget) => ({
			...widget,
			__rect: getWidgetRectPx(widget, metrics),
		}));

	const connections = [];

	for (let firstIndex = 0; firstIndex < waterWidgets.length; firstIndex += 1) {
		for (let secondIndex = firstIndex + 1; secondIndex < waterWidgets.length; secondIndex += 1) {
			const widgetA = waterWidgets[firstIndex];
			const widgetB = waterWidgets[secondIndex];
			const rectA = widgetA.__rect;
			const rectB = widgetB.__rect;

			const overlapY = getOverlapLength(rectA.top, rectA.top + rectA.height, rectB.top, rectB.top + rectB.height);
			const overlapX = getOverlapLength(rectA.left, rectA.left + rectA.width, rectB.left, rectB.left + rectB.width);

			const candidateConnections = [];

			if (overlapY >= getMinimumAlignmentOverlap(rectA.height, rectB.height)) {
				const gapRightToLeft = rectB.left - (rectA.left + rectA.width);
				const gapLeftToRight = rectA.left - (rectB.left + rectB.width);

				if (Math.abs(gapRightToLeft) <= tolerance) {
					candidateConnections.push(
						buildConnectionRecord(
							widgetA,
							widgetB,
							'horizontal',
							gapRightToLeft,
							overlapY,
							Math.max(rectA.top, rectB.top),
						),
					);
				}

				if (Math.abs(gapLeftToRight) <= tolerance) {
					candidateConnections.push(
						buildConnectionRecord(
							widgetB,
							widgetA,
							'horizontal',
							gapLeftToRight,
							overlapY,
							Math.max(rectA.top, rectB.top),
						),
					);
				}
			}

			if (overlapX >= getMinimumAlignmentOverlap(rectA.width, rectB.width)) {
				const gapBottomToTop = rectB.top - (rectA.top + rectA.height);
				const gapTopToBottom = rectA.top - (rectB.top + rectB.height);

				if (Math.abs(gapBottomToTop) <= tolerance) {
					candidateConnections.push(
						buildConnectionRecord(
							widgetA,
							widgetB,
							'vertical',
							gapBottomToTop,
							overlapX,
							Math.max(rectA.left, rectB.left),
						),
					);
				}

				if (Math.abs(gapTopToBottom) <= tolerance) {
					candidateConnections.push(
						buildConnectionRecord(
							widgetB,
							widgetA,
							'vertical',
							gapTopToBottom,
							overlapX,
							Math.max(rectA.left, rectB.left),
						),
					);
				}
			}

			if (candidateConnections.length === 0) {
				continue;
			}

			const bestConnection = candidateConnections.reduce((best, candidate) => (
				!best
				|| candidate.distance < best.distance
					? candidate
					: best
			), null);

			if (bestConnection) {
				connections.push(bestConnection);
			}
		}
	}

	return connections;
};
